// This context tracks the user's checkout state, including the shipping
// address and payment method.  It defaults to using Stripe as the
// preferred payment provider because Stripe offers lower fees on its
// free/small business plan.  The payment method can be changed later
// if additional providers are added.

'use client';

import { createContext, useContext, useState } from 'react';

interface CheckoutContextType {
  shippingAddress: ShippingAddress | null;
  paymentMethod: string | null;
  guestEmail: string | null;
  setShippingAddress: (address: ShippingAddress) => void;
  setPaymentMethod: (method: string) => void;
  setGuestEmail: (email: string) => void;
  clearCheckout: () => void;
}

interface ShippingAddress {
  address: string;
  city: string;
  postalCode: string;
  country: string;
}

const CheckoutContext = createContext<CheckoutContextType | null>(null);

export function CheckoutProvider({ children }: { children: React.ReactNode }) {
  const [shippingAddress, setShippingAddress] = useState<ShippingAddress | null>(null);
  // Default to Stripe as the sole supported payment method.
  const [paymentMethod, setPaymentMethod] = useState<string | null>('Stripe');
  const [guestEmail, setGuestEmail] = useState<string | null>(null);

  const clearCheckout = () => {
    setShippingAddress(null);
    setPaymentMethod(null);
    setGuestEmail(null);
  };

  return (
    <CheckoutContext.Provider
      value={{
        shippingAddress,
        paymentMethod,
        guestEmail,
        setShippingAddress,
        setPaymentMethod,
        setGuestEmail,
        clearCheckout,
      }}
    >
      {children}
    </CheckoutContext.Provider>
  );
}

export function useCheckout() {
  const context = useContext(CheckoutContext);
  if (!context) {
    throw new Error('useCheckout must be used within a CheckoutProvider');
  }
  return context;
}